import React, { useEffect, useId, useRef, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { AUTH_API_URL } from "../config";
import { saveSearch, storeSession } from "../lib/auth";
import { buttons, field } from "./site/ui";

const EMPTY_FORM = { firstName: "", lastName: "", email: "", password: "", confirmPassword: "" };

// Sign-in / sign-up dialog. When `vehicleData` is passed, that search is saved
// to the account as soon as the user signs in.
function AccountCreation({ onClose, vehicleData, onSuccess, initialMode = "signup" }) {
  const [isLogin, setIsLogin] = useState(initialMode === "login");
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const id = useId();
  const closeRef = useRef(onClose);

  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    setIsLogin(initialMode === "login");
  }, [initialMode]);

  // Close on Escape, stop the page behind from scrolling, and put focus back
  // on whatever opened the dialog once it closes.
  useEffect(() => {
    const opener = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeRef.current();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  const handleInputChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!isLogin && formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password,
          };
      const { data } = await axios.post(`${AUTH_API_URL}/api/auth/${isLogin ? "login" : "register"}`, payload);
      storeSession(data.token, data.user);

      let saved = false;
      if (vehicleData) {
        try {
          await saveSearch(vehicleData, data.token);
          saved = true;
        } catch (saveError) {
          console.error("Error saving search:", saveError);
        }
      }

      const done = isLogin ? "You're signed in." : "Your account has been created.";
      setSuccess(saved ? `${done} This report has been saved to My searches.` : done);
      setTimeout(() => (onSuccess ? onSuccess(data.user, { saved }) : closeRef.current()), 1200);
    } catch (requestError) {
      console.error("Account error:", requestError);
      if (requestError.response?.data?.message) setError(requestError.response.data.message);
      else if (requestError.request) setError("We couldn't reach the server. Please try again.");
      else setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    window.location.href = `${AUTH_API_URL}/api/auth/google`;
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError("");
    setSuccess("");
    setFormData(EMPTY_FORM);
  };

  let description = isLogin ? "Welcome back. Your saved reports are in My searches." : "Save reports and find them again later.";
  if (vehicleData) description = "Sign in or create an account and we'll save this report for you.";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        className="w-full max-w-md rounded-lg bg-white text-left shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <h2 id={`${id}-title`} className="text-lg font-semibold text-slate-900">
              {isLogin ? "Sign in" : "Create your account"}
            </h2>
            <p className="mt-1 text-sm text-slate-600">{description}</p>
          </div>
          <button type="button" onClick={onClose} className={`${buttons.ghost} -mr-2 -mt-1 p-2`}>
            <X className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </button>
        </div>

        <div className="px-6 py-6">
          {success ? (
            <p role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {success}
            </p>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor={`${id}-first`} className={field.label}>
                        First name
                      </label>
                      <input
                        id={`${id}-first`}
                        name="firstName"
                        autoFocus
                        autoComplete="given-name"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={field.input}
                      />
                    </div>
                    <div>
                      <label htmlFor={`${id}-last`} className={field.label}>
                        Last name
                      </label>
                      <input
                        id={`${id}-last`}
                        name="lastName"
                        autoComplete="family-name"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={field.input}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor={`${id}-email`} className={field.label}>
                    Email
                  </label>
                  <input
                    id={`${id}-email`}
                    type="email"
                    name="email"
                    autoFocus={isLogin}
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={field.input}
                  />
                </div>

                <div>
                  <label htmlFor={`${id}-password`} className={field.label}>
                    Password
                  </label>
                  <input
                    id={`${id}-password`}
                    type="password"
                    name="password"
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    minLength={isLogin ? undefined : 6}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={field.input}
                  />
                  {!isLogin && <p className={field.hint}>At least 6 characters.</p>}
                </div>

                {!isLogin && (
                  <div>
                    <label htmlFor={`${id}-confirm`} className={field.label}>
                      Confirm password
                    </label>
                    <input
                      id={`${id}-confirm`}
                      type="password"
                      name="confirmPassword"
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={field.input}
                    />
                  </div>
                )}

                {error && (
                  <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={loading} className={`${buttons.primary} w-full`}>
                  {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
                </button>
              </form>

              <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                <span className="h-px flex-1 bg-slate-200" />
                or
                <span className="h-px flex-1 bg-slate-200" />
              </div>

              <button type="button" onClick={handleGoogleSignIn} className={`${buttons.secondary} w-full`}>
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt=""
                  className="h-[18px] w-[18px]"
                />
                Continue with Google
              </button>

              <p className="mt-5 text-center text-sm text-slate-600">
                {isLogin ? "New to Car Sense? " : "Already have an account? "}
                <button type="button" onClick={toggleMode} className={buttons.link}>
                  {isLogin ? "Create an account" : "Sign in"}
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountCreation;
